<template>
  <div
    class="lpu-schedule-days"
    :class="{ 'lpu-schedule-days--v5': ff_lpu_branding_page_v5 }"
  >
    <div
      class="lpu-schedule-days__fog lpu-schedule-days__fog--left"
      :class="{ 'lpu-schedule-days__fog--visible': !isAtStart }"
    />

    <CoreSwiper
      ref="swiperRef"
      free-mode
      slides-per-view="auto"
      :has-pagination="false"
      :has-navigation="!isMobile"
      :space-between="8"
      :placeholder-min-height="66"
    >
      <template #slides="{ slideAttrs }">
        <div
          v-for="item in items"
          :key="item.date"
          v-bind="slideAttrs"
          class="lpu-schedule-days__slide"
          @click="onDayClick(item)"
        >
          <SelectableTab
            small
            class="lpu-schedule-days__tab"
            :title="item.title"
            :subtitle="item.subtitle"
            :label="item.label"
            :active="item.date === activeDate"
            :disabled="item.disabled"
          >
            <template
              v-if="isWeekend(item.subtitle) && item.date !== activeDate && !item.disabled"
              #subtitle
            >
              <span class="text-error--text">
                {{ item.subtitle }}
              </span>
            </template>
          </SelectableTab>
        </div>
      </template>
    </CoreSwiper>

    <div
      class="lpu-schedule-days__fog lpu-schedule-days__fog--right"
      :class="{ 'lpu-schedule-days__fog--visible': !isAtEnd }"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Полоска дней календаря записи на странице клиники.
 *
 * Не привязана к секции врачей: дни и активную дату задаёт родитель.
 */
import {
  computed,
  ref,
  watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import type Swiper from 'swiper/core'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import SelectableTab from 'src/components/common/SelectableTab/SelectableTab.vue'
import type { ScheduleDayItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'

type Emits = {
  (_event: 'select', _date: string): void
}

type SwiperWithAllowClick = Swiper & {
  allowClick?: boolean
}

type CoreSwiperExpose = {
  swiperInstance: SwiperWithAllowClick | null
}

defineProps<{
  items: ScheduleDayItem[]
  activeDate?: string
}>()

const emit = defineEmits<Emits>()

const { context }: CommonContextStore = useContextStore()
const ff_lpu_branding_page_v5 = computed(() => (
  Boolean(context.request.feature_flags.ff_lpu_branding_page_v5)
))

const isMobile = useIsMobile()
const swiperRef = ref<CoreSwiperExpose | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(true)

function getSwiper() {
  return swiperRef.value?.swiperInstance ?? null
}

function isWeekend(subtitle: string) {
  return subtitle === 'Сб' || subtitle === 'Вс'
}

function onDayClick(item: ScheduleDayItem) {
  // После драга Swiper гасит allowClick
  if (item.disabled || getSwiper()?.allowClick === false) {
    return
  }

  emit('select', item.date)
}

watch(
  () => getSwiper(),
  (swiper, _prev, onCleanup) => {
    if (!swiper) {
      return
    }

    const syncEdges = () => {
      isAtStart.value = swiper.isBeginning
      isAtEnd.value = swiper.isEnd
    }

    syncEdges()
    swiper.on('progress', syncEdges)
    swiper.on('resize', syncEdges)

    onCleanup(() => {
      swiper.off('progress', syncEdges)
      swiper.off('resize', syncEdges)
    })
  },
)
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

$nav-btn-size: 32px;
$nav-btn-half: $nav-btn-size / 2;

// Legend «Сегодня»/«Завтра» сидит сверху таба и тянет геометрический центр выше корпуса
$nav-center-offset: 4px;
$fog-gradient: $ui-kit-bg-gray-0 0.71%,
  rgba(255, 255, 255, 0.81) 12%,
  rgba(255, 255, 255, 0.38) 32%,
  rgba(255, 255, 255, 0) 43%;

.lpu-schedule-days {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-top: -8px;

  &__slide {
    width: auto;
  }

  &__tab {
    border-radius: 15px;
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    z-index: 3;
    top: calc(50% + #{$nav-center-offset});
    margin-top: 0;
    width: $nav-btn-size;
    height: $nav-btn-size;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    transform: translateY(-50%);

    span {
      font-size: 20px;
      color: var(--lpu-branding-color-primary, $ui-kit-primary) !important;
    }
  }

  :deep(.swiper-button-prev) {
    left: -$nav-btn-half;
    right: auto;
  }

  :deep(.swiper-button-next) {
    right: -$nav-btn-half;
    left: auto;
  }

  :deep(.swiper-button-disabled) {
    opacity: 0 !important;
    pointer-events: none !important;
  }

  &:hover {
    :deep(.swiper-button-prev:not(.swiper-button-disabled)),
    :deep(.swiper-button-next:not(.swiper-button-disabled)) {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__fog {
    pointer-events: none;
    z-index: 2;
    width: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    transition: width 0.1s linear;
  }

  &__fog--visible {
    width: 48px;
  }

  &__fog--left {
    left: 0;
    background: linear-gradient(90deg, $fog-gradient);
  }

  &__fog--right {
    right: 0;
    background: linear-gradient(270deg, $fog-gradient);
  }
}

:deep(.lpu-schedule-days__tab.selectable-tab--active) {
  color: var(--lpu-branding-color-primary, $ui-kit-primary);
  border-color: var(--lpu-branding-color-primary, $ui-kit-primary);
  background-color: $ui-kit-bg-gray-0;
}

.lpu-schedule-days--v5 {
  :deep(.lpu-schedule-days__tab.selectable-tab--active) {
    color: var(--lpu-branding-color-text, $ui-kit-primary);
    border-color: var(--lpu-branding-color-text, $ui-kit-primary);
  }
}
</style>
